import React, {Fragment, useState, useEffect, useCallback} from 'react'
import {ReactComponent as ChevronRight} from '../../assets/icons/chevron-right.svg'
import {ReactComponent as ArrowBack} from '../../assets/icons/arrow_back.svg'
import {useSelectedVariantContext} from './context/SelectedVariantContext'
import {
    actionChangeDisplay,
    actionChangeSelectedLayer,
    actionSetRunningImageCombine,
    actionSetRunningImageCombinedMode
} from './context/SelectedVariantContext/action'
import {toaster} from '../../shared/PFToast'
import {isBackofficeEnv} from '../../helpers/isBackoffceEnv'
import Publish from './publish/Publish'
import { useEditorStageSides } from '../../hooks/useEditorStageSides'
import { StepIndicator } from '../shopify-mapping/header/MappingHeader'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from 'reactstrap'
import {mergeAttributesByDesignEdited} from './context/SelectedVariantContext/helper'
import {usePreviewArtworks} from '../../hooks/usePreviewArtworks'
import DesignModeSelector from './DesignModeSelector'
import { DESIGN_MODES } from './Step2Container'
import PubSub from './Broker'

const NavBar = ({mappingProductId, variantId, sku, showDesignModeTooltip}) => {
    const {
        state: {display, pickerAttributes, selectedAttribute, productDetail, runningImageCompress, productCreatedType, cropMode, designs, mappingProduct, product, designMode, currentBgImage, applyBgAllSide},
        dispatch,
    } = useSelectedVariantContext()
    const {fetchCapturedSides} = usePreviewArtworks()
    const navigate = useNavigate()
    const [tooltipOpen, setTooltipOpen] = useState(false)
    const [coverageWarnings, setCoverageWarnings] = useState({})
    const toggleTooltip = () => setTooltipOpen(!tooltipOpen)
    const stageSides = useEditorStageSides()

    // Subscribe to artwork coverage check results from ImageLayer
    useEffect(() => {
        const token = PubSub.subscribe('ARTWORK_COVERAGE_CHECK', (data) => {
            const {side, result} = data || {}
            if (side && result) setCoverageWarnings((prev) => ({...prev, [side]: result}))
        })
        return () => PubSub.unsubscribe(token)
    }, [])
    const hasLayers = stageSides.some((side) =>
        side.some((stage) => (stage.layers?.length > 0 && (designMode === DESIGN_MODES.COMBINED ? stage.side === 'all-in-one' : stage.side !== 'all-in-one')))
    )

    const isDesignStep = display === 'design'
    const isPreviewStep = display === 'preview'
    const isPricingStep = display === 'pricing'

    const toggleDisplay = (_display) => {
        if (_display !== display) {
            dispatch(actionChangeDisplay(_display))
        }
    }

    const switchPreview = () => {
        let invalidDpi = null
        for (const attr of pickerAttributes) {
            const {safeZone} = attr

            for (const [key, value] of Object.entries(safeZone)) {
                const totalInvalidDpi = value.invalidsDpi.length
                if (totalInvalidDpi) {
                    invalidDpi = {
                        side: key,
                        total: totalInvalidDpi,
                    }
                    break
                }
            }
        }
        if (invalidDpi) {
            toaster({
                type: 'error',
                message: `Side ${invalidDpi.side} has ${invalidDpi.total} layer image invalid DPI`,
            })
        } else {
            // Block navigation if any side has coverage failure.
            // A side is exempt from coverage check if it has a background that fully covers the canvas:
            //   • currentBgImage.file — background IMAGE applied globally (covers ALL sides)
            //   • background.isUserSetFill — user explicitly chose a bg color for this side
            //   • applyBgAllSide — user applied bg color to all placements at once
            // NOTE: background.fill alone cannot be used — it defaults to defaultMaterialColor (#fff)
            //       so every side would appear to have a BG even when the user hasn't set one.
            if (!currentBgImage?.file) {
                // Collect every side that has at least one image layer (source of truth: stageSides).
                // This catches sides the user never visited (ImageLayer never mounted → no cached result).
                const sidesWithImageLayer = new Set()
                for (const attrGroup of stageSides) {
                    for (const stage of attrGroup) {
                        const sideName = stage.side
                        // Respect combined vs. individual design mode
                        if (designMode === DESIGN_MODES.COMBINED && sideName !== 'all-in-one') continue
                        if (designMode !== DESIGN_MODES.COMBINED && sideName === 'all-in-one') continue
                        const hasImage = (stage.layers || []).some((l) => l.layerType === 'image')
                        if (hasImage) sidesWithImageLayer.add(sideName)
                    }
                }

                // A side fails if: no cached result yet (unvisited = unverified) OR result.ok === false.
                // Exception: skip sides where the user has explicitly set a bg color (isUserSetFill).
                const failedSides = [...sidesWithImageLayer].filter((side) => {
                    // applyBgAllSide = user applied bg color to ALL sides → every side is covered
                    if (applyBgAllSide) return false
                    // Check if user explicitly set a bg color for this specific side
                    const stageBg = stageSides.flatMap((g) => g).find((s) => s.side === side)?.background
                    if (stageBg?.isUserSetFill) return false
                    // No user-set bg → check coverage result
                    const result = coverageWarnings[side]
                    return !result || !result.ok
                })

                if (failedSides.length > 0) {
                    // Pass the authoritative list so the warning component renders exactly these sides.
                    // pubsub.js publish 2nd arg must be an ARRAY of arguments to spread into callback.
                    PubSub.publish('ARTWORK_COVERAGE_BLOCK_NEXT', [{failedSides}])
                    return
                }
            }

            // All sides passed — clear any previously shown warning banner before navigating
            PubSub.publish('ARTWORK_COVERAGE_CLEAR', [{}])

            dispatch(actionChangeSelectedLayer(null))
            toggleDisplay('preview')
            if (designMode === DESIGN_MODES.COMBINED) {
                dispatch(actionSetRunningImageCombinedMode())
                designs.forEach(design => {
                    design.side['all-in-one'].isSideEdited = true
                    design.isDesignEdited = true
                })
            } else {
                dispatch(actionSetRunningImageCombine(true))
            }
            const fetchedDesigns = mergeAttributesByDesignEdited(pickerAttributes, designs)
            fetchCapturedSides(fetchedDesigns, true)
        }
    }

    const backToProductList = () => {
        const confirmed = window.confirm('Changes you made may not be saved.')
        if (confirmed) {
            if (mappingProductId) {
                navigate(`/mapping-variants/${mappingProductId}?variantId=${variantId}`)
            }
            else if (isBackofficeEnv()) {
                window.open(`${process.env.REACT_APP_BASE_URL}/a/products/mockup-generator/variants`, '_self')
            } else {
                window.open(`${process.env.REACT_APP_WEBSITE_URL}/product/${productDetail.slug}`, '_self')
            }
        }
    }

    const handleClickNextBtn = () => {
        switchPreview()
    }

    const handleNavigate = () => {
        if (mappingProductId) {
            backToMapping()
            return
        }
        return isPreviewStep || isPricingStep ? toggleDisplay('design') : backToProductList()
    }

    const backToMapping = () => {
        navigate(`/mapping-variants/${mappingProductId}?variantId=${variantId}`, {replace: true})
    }

    const renderSaveButton = () => {
        if (mappingProductId && mappingProduct) {
            let variant = product.variants?.find((v) => v.sku === sku)
            if (!variant) {
                variant = mappingProduct.variant
            }
            let title = variant.attributes?.map((opt) => opt.value_text).join(' / ') || ''
            let color = null
            const colorOption = variant?.attributes?.find((attr) => attr.type === 'color')
            if (colorOption) {
                color = colorOption.color_code || colorOption.value_code
            }
            return (
                <Fragment>
                    <StepIndicator activeStep={2} />
                    <div className="attribute" id="variant-info-tooltip">
                        Variant:&nbsp;
                        {color && <span className="colorPreview" style={{'--bg-color': color}} />}
                        <span className="variant">{title || ''}</span>
                        <span className="price">${variant.base_cost || 0}</span>
                    </div>
                    <Tooltip
                        placement="bottom"
                        isOpen={tooltipOpen}
                        target="variant-info-tooltip"
                        toggle={toggleTooltip}
                    >
                        {title || 'No variant title'}
                    </Tooltip>
                    {isDesignStep && <DesignModeSelector showDesignModeTooltip={showDesignModeTooltip} />}
                    <Publish disabled={!hasLayers}/>
                </Fragment>
            )
        }
        return (
            <Fragment>
                {isDesignStep && <DesignModeSelector showDesignModeTooltip={showDesignModeTooltip} />}
                {isDesignStep &&
                productCreatedType !== 'regularHasTemplate' &&
                productCreatedType !== 'regularNoTemplate' ? (
                    <button
                        id={'header_next_btn'}
                        type={'button'}
                        className={'Design btn btn-primary flex-center rounded'}
                        disabled={runningImageCompress || !hasLayers || cropMode}
                        onClick={handleClickNextBtn}
                    >
                        Next <ChevronRight className={'ms-1'} />
                    </button>
                ) : (
                    <Publish />
                )}
            </Fragment>
        )
    }

    return (
        <div className="NavBar2 no-gutters">
            <div className="Left col-4 text-left d-flex align-items-center">
                {
                    isPreviewStep || isPricingStep || mappingProductId ? (
                        <span className='PrevStep cursor-pointer flex-center' onClick={handleNavigate}>
                            <span className='Icon'>
                                <ArrowBack />
                            </span>
                            {
                                mappingProductId ? (
                                    <span className={'text-primary'}>Select variant</span>
                                ) : isPreviewStep || isPricingStep ? (
                                    <span className={'text-primary'}>Design</span>
                                ) : (
                                    <a href='/'>Product</a>
                                )
                            }
                        </span>
                    ) : (
                        <a href="/" className='PrevStep cursor-pointer flex-center text-primary text-decoration-none'>
                            <span className='Icon'>
                                <ArrowBack />
                            </span>
                            Product
                        </a>
                    )
                }
                <div className={'vr mx-3'} />
                <h6 className={'fw-500 mb-0'}>{productDetail.title}</h6>
                {/*<ChangeProductButton/>*/}
            </div>

            <div className='Right col-8'>
                <div className={'d-flex justify-content-end'}>
                    {renderSaveButton()}
                </div>
            </div>
        </div>
    )
}

export default NavBar



